import { ReactElement,  useEffect,  useRef, useState } from "react";
import { Group, Mesh, MeshStandardMaterial } from "three";

import { useNavigate } from "react-router-dom";

import { Scene } from "components/ThreeItems/Scene";
import { CanvasProvider } from "components/ThreeItems/CanvasProvider";
import { motion } from "framer-motion"
import {Layers, PaintbrushIcon, PaletteIcon, SaveIcon } from "lucide-react";
import { useLangContext } from "context/Lang";
import { useAvatarEditorContext } from "../avatar-editor-provider";
import { Button } from "components/Button/Button";
import { AsyncProcess } from "services/AsyncProcess";
import { routes } from "routes";
import { setToast } from "storeRedux/slices/toast";
import { useDispatch } from "react-redux";





type AvatarEditorEnvironmentProps = {
    canEdit?: boolean,
    inDialog?:boolean
}

const SKIN_PALETTE = ["#ffdbac", "#f1c27d", "#e0ac69", "#c68642", "#8d5524"];
const EYES_PALETTE = ["#1a1a1a", "#2f3640", "#487eb0", "#00f3ff", "#ff007f"];
const CLOTHES_PALETTE = ["#00f3ff", "#ff007f", "#ffaa00", "#4cd137", "#487eb0", "#2f3640", "#ffffff"];
const PANTS_PALETTE = ["#1a1a1a", "#2f3640", "#487eb0", "#00f3ff", "#ff007f"];

const AVATAR_EDITABLE_PARTS = [
    { key: "head", nameKey: "avatar-head", palette: SKIN_PALETTE },
    { key: "eyes", nameKey: "avatar-eyes", palette: EYES_PALETTE },
    { key: "torso", nameKey: "avatar-torso", palette: CLOTHES_PALETTE },
    { key: "arms", nameKey: "avatar-arms", palette: CLOTHES_PALETTE }, 
    { key: "legs", nameKey: "avatar-legs", palette: PANTS_PALETTE }   
];


export const AvatarEditorEnvironment = ({ canEdit,inDialog }: AvatarEditorEnvironmentProps): ReactElement => {

    const isSelectedMeshRef = useRef<boolean>(false);
    const { gW } = useLangContext();
    const [loading,setLoading] = useState<boolean>(false);
    const {
  

        handleLoadScene,
    } = useAvatarEditorContext();


    const dispatch= useDispatch();



    const [sceneGroup,setSceneGroup]=useState<Group|null>(null)
    const [avatarColors, setAvatarColors] = useState<Record<string, string>>({
        head: "#ffdbac",
        eyes: "#1a1a1a",
        torso: "#00f3ff",
        arms: "#00f3ff",
        legs: "#1a1a1a"
    });



    const updatePartColor = (partKey: string, color: string) => {
        
        if (!sceneGroup) return;

        setAvatarColors(prev => ({ ...prev, [partKey]: color }));

        sceneGroup.traverse((object) => {
            if (object instanceof Mesh) {
                const material = object.material as MeshStandardMaterial;
                const name = object.name.toLowerCase();
               
                const isMatch = 
                    (partKey === "head" && name.includes("head")) ||
                    (partKey === "eyes" && name.includes("eye")) ||
                    (partKey === "torso" && name.includes("torso"))||
                    (partKey === "arms" && name.includes("arm")) ||
                    (partKey === "legs" && name.includes("leg"));


                
                
                if (isMatch && material) {
                 
                    
                    material.color.set(color);
                }
            }
        });
    };


    useEffect(()=>{

        sceneGroup&&AsyncProcess({
            url:routes.api.avatarColor,
            data:avatarColors,
            setError:()=>{
                dispatch(
                    setToast({
                        detail: gW("happen-error"),
                        severity: "error",
                    })
                )
            },
            effectProcess:[(res)=>{
                if(res?.success===false){
                    dispatch(
                        setToast({
                            detail: gW(res?.message),
                            severity: "error",
                        })
                    )
                }else {
                    setAvatarColors(res?.data||avatarColors);

                    Object.keys(res?.data||{})?.forEach((key)=>{
                        updatePartColor(key,res?.data?.[key]);
                    })
                }
            }]
        })

    },[sceneGroup]);


    const handleSaveColors=()=>{
        AsyncProcess({
            type:"post",
            url:routes.api.avatarColor,
            data:avatarColors,
            setError:()=>{
                dispatch(
                    setToast({
                        detail: gW("happen-error"),
                        severity: "error",
                    })
                )
            },
            effectProcess:[(res)=>{
                if(res?.success===false){
                    dispatch(
                        setToast({
                            detail: gW(res?.message),
                            severity: "error",
                        })
                    )
                }
            }]
        })
    }


    return <div className="  " >



        <motion.div
            initial={{ x: 100, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            className=" bg-primary-900/70  backdrop-blur-xl rounded-2xl border border-white/10 select-none h-[calc(100%-64px)] top-[64px] overflow-y-auto w-[360px] flex flex-col gap-4 absolute end-4 z-50 p-4 shadow-2xl text-white"
        >
            <div className="flex items-center gap-3 border-b border-white/5 pb-3">
                <div className="p-2.5 bg-primary/10 rounded-xl text-primary-200 border border-primary/20">
                    <Layers size={20} />
                </div>
                <div className="flex flex-col">
                    <span className="text-[10px] uppercase tracking-widest text-primary-400 font-bold">
                        {gW("avatar-customizer") }
                    </span>
                    <h3 className="text-sm font-semibold text-white">
                        {gW("avatar-style-panel") || "Appearance Customization"}
                    </h3>
                </div>
                <Button
                    variant="outline"
                    onClick={handleSaveColors}
                    className=" !border-primary-200 !text-primary-50 hover:bg-primary-900  "
                >
                    <SaveIcon/>
                    {/* {gW("save")} */}
                </Button>
            </div>

            <div className="flex flex-col gap-4 overflow-y-auto pr-1">
                {AVATAR_EDITABLE_PARTS.map((part) => (
                    <div key={part.key} className="flex flex-col gap-2 bg-white/5 p-3 rounded-xl border border-white/5">
                        <div className="flex items-center justify-between text-xs text-primary-100 font-semibold mb-1">
                            <div className="flex items-center gap-2">
                                <PaletteIcon size={14} className="text-primary-200" />
                                <span>{gW(part.nameKey) || part.key}</span>
                            </div>
                            
                            <label className="flex items-center gap-1 cursor-pointer opacity-70 hover:opacity-100 transition-opacity">
                                <PaintbrushIcon size={12} className="text-primary-200" />
                                <span className="text-[10px] text-primary-100 font-normal">{gW("custom") || "Custom"}</span>
                                <input 
                                    type="color" 
                                    value={avatarColors?.[part.key]} 
                                    onChange={(e) => updatePartColor(part.key, e.target.value)}
                                    className="w-0 h-0 opacity-0 absolute pointer-events-none" 
                                />
                                <div 
                                    style={{ backgroundColor: avatarColors?.[part.key] }}
                                    className="w-4 h-4 rounded border border-white/40 ml-1 shadow-inner" 
                                />
                            </label>
                        </div>
                        
                        <div className="flex flex-wrap gap-2">
                            {part.palette.map((color) => {
                                const isSelected = avatarColors?.[part.key]?.toLowerCase() === color.toLowerCase();
                                return (
                                    <button
                                        key={color}
                                        onClick={() => updatePartColor(part.key, color)}
                                        style={{ backgroundColor: color }}
                                        className={`w-8 h-8 rounded-full cursor-pointer transition-all duration-200 transform hover:scale-110 active:scale-95 ${
                                            isSelected 
                                                ? "ring-2 ring-primary-400 ring-offset-2 ring-offset-primary-200" 
                                                : "border border-white/20"
                                        }`}
                                    />
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>
        </motion.div>
     

        <CanvasProvider 
            className=" z-0 absolute top-0 left-0  h-full   w-full " 
            activePlaneHelper={false} 
            showSky={false} 
            planeShowHelper={true} 
            planeScale={[100, 100, 0]}
            gW={gW}
            hiddenPlayButton={true}
        >
            <Scene
                

                onLoad={(e) => handleLoadScene?.(e as SceneItemsProps,(e)=>{
                    setSceneGroup(e);
                })}
                isActiveSelect={false}
                showHelper={false}
            />
        </CanvasProvider>
    </div>
}




